import React, { useEffect, useMemo, useState } from 'react';
import { IModGroupModifierItem, RestaurantModGroupPropertiesType } from 'types';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { ItemList } from 'components/ViewPage//ItemList';
import style from './PortionFlow.module.scss';

interface IProps {
	parentChildRelations: IModGroupModifierItem[];
}

export const PortionFlow: React.FC<IProps> = React.memo(({ parentChildRelations }) => {
	const [selectedDishes, setSelectedDishes] = useState<Nullable<string>>(null);
	const mappedDishes = useMemo(() => {
		const allItems = parentChildRelations.map((children) => ({
			id: children.parentId,
			name: children.parentName,
		}));
		return Array.from(new Map(allItems.map((item) => [item.id, item])).values());
	}, [parentChildRelations]);

	const [selectedPortions, setSelectedPortions] = useState<Nullable<string>>(null);
	const mappedPortions = useMemo(() => {
		const relevantChildren = selectedDishes
			? parentChildRelations.filter((children) => children.parentId === selectedDishes)
			: parentChildRelations;

		return Array.from(
			new Map(
				relevantChildren.map((children) => [
					children.portionId,
					{ id: children.portionId, name: children.portionName },
				]),
			).values(),
		);
	}, [selectedDishes, parentChildRelations]);

	const initialMappedModifiers = useMemo(
		() =>
			Array.from(
				new Map(
					parentChildRelations.map((children) => [
						`${children.modifierId}__${children.modifierType}`,
						{
							id: children.modifierId,
							name: children.modifierName,
							parentId: children.parentId,
							portionId: children.portionId,
							modifierType: children.modifierType,
							pathToItem: [
								RestaurantModGroupPropertiesType.Dish,
								RestaurantModGroupPropertiesType.DishAndModifier,
							].includes(children.modifierType)
								? '/menu-v2/dishes/view?dishID='
								: '/menu-v2/modifiers/view?modifierID=',
						},
					]),
				).values(),
			),
		[parentChildRelations],
	);
	const filteredMappedModifiers = useMemo(() => {
		if (!selectedDishes && !selectedPortions) {
			const mappedModifiers = Array.from(
				new Map(
					parentChildRelations.map((children) => [
						`${children.modifierId}__${children.modifierType}`,
						{
							id: children.modifierId,
							name: children.modifierName,
							parentId: children.parentId,
							portionId: children.portionId,
							modifierType: children.modifierType,
							pathToItem: [
								RestaurantModGroupPropertiesType.Dish,
								RestaurantModGroupPropertiesType.DishAndModifier,
							].includes(children.modifierType)
								? '/menu-v2/dishes/view?dishID='
								: '/menu-v2/modifiers/view?modifierID=',
						},
					]),
				).values(),
			);
			return mappedModifiers;
		} else if (selectedDishes && !selectedPortions) {
			const mappedModifiers = Array.from(
				new Map(
					parentChildRelations.map((children) => [
						`${children.modifierId}__${children.modifierType}__${children.parentId}`,
						{
							id: children.modifierId,
							name: children.modifierName,
							parentId: children.parentId,
							portionId: children.portionId,
							modifierType: children.modifierType,
							pathToItem: [
								RestaurantModGroupPropertiesType.Dish,
								RestaurantModGroupPropertiesType.DishAndModifier,
							].includes(children.modifierType)
								? '/menu-v2/dishes/view?dishID='
								: '/menu-v2/modifiers/view?modifierID=',
						},
					]),
				).values(),
			);
			return mappedModifiers.filter((modifier) => {
				const dishMatch = selectedDishes?.includes(modifier.parentId);
				return dishMatch;
			});
		}

		const mappedModifiers = parentChildRelations.map((children) => ({
			id: children.modifierId,
			name: children.modifierName,
			parentId: children.parentId,
			portionId: children.portionId,
			modifierType: children.modifierType,
			pathToItem: [
				RestaurantModGroupPropertiesType.Dish,
				RestaurantModGroupPropertiesType.DishAndModifier,
			].includes(children.modifierType)
				? '/menu-v2/dishes/view?dishID='
				: '/menu-v2/modifiers/view?modifierID=',
		}));
		return mappedModifiers.filter((modifier) => {
			const dishMatch = selectedDishes?.includes(modifier.parentId);
			const portionMatch = selectedPortions?.includes(modifier.portionId);
			if (selectedDishes && selectedPortions) {
				return dishMatch && portionMatch;
			}
			return dishMatch || portionMatch;
		});
	}, [initialMappedModifiers, selectedDishes, selectedPortions]);

	useEffect(() => {
		setSelectedPortions(null);
	}, [selectedDishes]);

	return (
		<div className={style.container}>
			<div className={style.selectWrapper}>
				<OutsideFormSelect
					testId="elementsThatIncludeThisGroup"
					label="elementsThatIncludeThisGroupLabel"
					name="elementsThatIncludeThisGroup"
					value={selectedDishes}
					options={mappedDishes}
					onChange={setSelectedDishes}
				/>
				<OutsideFormSelect
					testId="portion"
					label="portionLabel"
					name="portion"
					value={selectedPortions}
					options={mappedPortions}
					onChange={setSelectedPortions}
				/>
			</div>
			{!!filteredMappedModifiers.length && (
				<ItemList items={filteredMappedModifiers} assignBtnTranslationKey="editRestaurantsList" />
			)}
		</div>
	);
});
